/**
 * The MERCHANT-IDENTITY axis of delete-and-replace recognition — axis 2 of three.
 *
 * ADR: delete-and-replace-recognition orders the axes by AUTHORITY: **link → identity → recency.**
 * The aggregator link (axis 1, in `suggestionTools.ts`) is the provider's own statement and needs no
 * scoring. This file is what runs when there is no link: it asks whether a posted row is plausibly
 * the settled version of a retracted one, from the two descriptions alone.
 *
 * ⚠ IT IS NOT A SIMILARITY SCORE, AND THAT IS THE WHOLE POINT. The metric it replaces was the Dice
 * coefficient over character bigrams, whose discrimination ratio was measured **flat at 1.2–1.7
 * across every threshold from 0.2 to 0.9** against a 30,000-pair control — i.e. loosening it traded
 * missed replacements for false adoptions at roughly 1:1. Bigram overlap measures string SURFACE, so
 * it failed in both directions at once:
 *
 *  - it **rewarded boilerplate** — two genuinely different transfers sharing
 *    `ONLINE TRANSFER TO CHK …4417 TRANSACTION#: ` scored 0.833, and pairing them destroys a real
 *    transfer;
 *  - it **punished informative additions** — an identical merchant plus a city and ZIP scored 0.526
 *    and was rejected.
 *
 * Do not arrive here and start by moving a constant. The ADR's "Alternatives considered" records the
 * measurement that rules that out.
 *
 * ## The two rules, and why each is shaped the way it is
 *
 * **1. A conflicting IDENTIFIER disqualifies the pair outright, at any similarity.** A bank's own
 * per-transaction id (`TRANSACTION#`, `REF`, `AUTH`, …) is the one field in a description that is
 * *definitionally* unique per transaction, so two rows stating DIFFERENT ones are two different
 * transactions however alike they read. This is the strongest signal available in either direction,
 * and it is the only thing that separates the corpus's `MUST NOT PAIR` case — whose merchant words
 * agree PERFECTLY (containment 1.0) because everything except the id is boilerplate.
 *
 * **2. Agreement is scored by CONTAINMENT, not a symmetric coefficient.** The posted row routinely
 * *adds* tokens the pending never carried — store number, city, state, ZIP — and those additions
 * carry no evidence against identity. A symmetric measure charges the pair for them (that is the
 * 0.526 rejection above); containment does not. It runs in both directions because the retracted row
 * can carry the extra token too (the reported D3b case has a trailing `USA` where the posted row has
 * a state code that normalizes away).
 *
 * **3. …but a RATIO is not enough on its own, so a minimum number of words must actually agree.**
 * Containment 0.5 over two tokens is a single shared word, which would pair `BLUE BOTTLE` with
 * `BLUE APRON`. `MIN_AGREED_MERCHANT_TOKENS` guards that end; `MIN_MERCHANT_CONTAINMENT` guards the
 * other (a couple of incidental hits inside a long description).
 *
 * @see merchantTokens.ts for the tokenizer, which is shared with the bill/income detector.
 */
import { merchantTokensAgree, normalizeDescriptionTokens } from "./merchantTokens.js";
/**
 * The classes of bank-supplied per-transaction identifier we recognize, each with the CONTEXT
 * KEYWORDS that announce one.
 *
 * ⚠ CONTEXT-BASED, NEVER DIGIT-COUNT — this is a load-bearing correction, not a stylistic
 * preference. A prototype classified any 5+-digit run as an identifier and promptly read a ZIP code
 * (`97214`, on a real bar-tab replacement) as a transaction id, disqualifying a genuine replacement.
 * Six digits happened to work on the sample, which is a LENGTH heuristic standing in for a TYPE.
 * A bare number is a store number, a ZIP, a phone fragment, an amount, or an id — the keyword in
 * front of it is the only thing that says which, so an unannounced number is never an identifier
 * here. `deleteReplaceIdentity.test.ts` pins the ZIP case.
 *
 * Grouped by class because a conflict is only meaningful WITHIN a class: one row quoting an `AUTH`
 * and the other quoting a `REF` are not stating contradictory values of the same field.
 *
 * ⚠ A KEYWORD MAY CARRY AN ORDINAL (`Ref1:`, `Ref2:`), AND MISSING THAT SWITCHED THE WHOLE RULE OFF
 * FOR AN ENTIRE INSTITUTION CLASS. Several credit unions stamp every PENDING with
 * `<merchant> / Ref1: <digits> Ref2: <digits> Ref3: <digits> <nn> Expires: <digits>`. `\bref\b` does
 * not match `ref1`, so none of those three per-transaction ids was read, `hasConflictingIdentifier`
 * returned false, and two pendings for DIFFERENT merchants paired — measured in prod on 2026-09-11,
 * where a ~$3 pending's decision was adopted onto a ~$154 subscription charge (T-403). Every Ref value
 * differs between those rows, so reading them is the whole fix.
 *
 * The ordinal is part of the CLASS KEY, not of the value: `Ref1` and `Ref2` are different FIELDS, so a
 * row quoting only `Ref1` and one quoting only `Ref2` state nothing contradictory — the same reasoning
 * that keeps `AUTH` from contradicting `REF`. Keying them together would manufacture a conflict out of
 * two banks' field ordering.
 *
 * ⚠ **THE KNOWN COST, AND IT FAILS OPEN — the direction this module usually refuses.** A feed that wrote
 * `Ref: 123` on one row and `Ref1: 456` on the other states the same field twice and would NOT conflict,
 * because the two land in different classes. That is the `AUTH NO12345` vs `AUTH NO 12345` shape one
 * level up, and here it permits an adoption rather than blocking one. Left as-is on evidence, not on
 * principle: the template that motivated the ordinal is PENDING-only and writes all three ordinals every
 * time, so no observed feed mixes the two renderings. Revisit if one ever does.
 */
const IDENTIFIER_KEYWORDS_BY_CLASS = {
    transaction: ["transaction", "trans", "txn", "tran"],
    reference: ["reference", "ref"],
    authorization: ["authorization", "auth"],
    trace: ["trace"],
    confirmation: ["confirmation", "conf"],
};
/**
 * Shortest value we will treat as an identifier.
 *
 * Not a classifier (the keyword already did that job) — a guard against reading a keyword's ordinary
 * English neighbour as its value. It works with `IDENTIFIER_VALUE_MUST_CONTAIN_DIGIT` below.
 */
const MIN_IDENTIFIER_VALUE_CHARS = 4;
/**
 * An identifier VALUE must contain a digit.
 *
 * This is not the digit-count heuristic the class doc warns about: the keyword still decides what is
 * an identifier, and this only rejects a keyword's prose neighbour. Without it,
 * `"Pre auth   BETOS EAST LOC:"` reads `auth = "betos"` — harmless today (a class must appear on BOTH
 * sides to conflict, and the row it pairs with has no `auth` at all) but a live trap the moment a
 * feed starts prefixing settlements with `AUTH`. Every real identifier in the measured population
 * carries digits.
 */
const IDENTIFIER_VALUE_MUST_CONTAIN_DIGIT = true;
/**
 * Filler words that sit between a keyword and its value — `AUTH **NO** 123456`, `REF **NUMBER** 998877`.
 * Skipped when standalone, and stripped when glued to the value (`AUTH NO12345`, `TRACE NO.5551212`).
 *
 * ⚠ The glued case matters because the disqualification is UNCONDITIONAL: without stripping it,
 * `AUTH NO12345` reads `no12345` while `AUTH NO 12345` reads `12345`, so the same identifier written
 * two ways contradicted itself and declined the pair outright.
 *
 * ⚠ THE SEPARATOR AFTER THE FILLER IS PART OF THE SAME HOLE, AND A DIGIT-ONLY LOOKAHEAD MISSED IT.
 * The first version required a digit IMMEDIATELY after the filler word, so any punctuation between
 * the two defeated it — including `.` and `-`, which `LEADING_SEPARATORS` already handled:
 * `TRACE NO.5551212` read `no.5551212` against `TRACE NO. 5551212`'s `5551212`. That format is named
 * three docblocks down as an observed real one. It consumes a run of NON-ALPHANUMERICS for the same
 * reason the separator sets below do — see their docblock.
 */
const IDENTIFIER_FILLER_WORDS = new Set(["no", "nos", "num", "nbr", "number"]);
const FILLER_GLUED_TO_VALUE = /^(?:number|nos|nbr|num|no)[^a-zA-Z0-9]*(?=\d)/;
/**
 * How many words after the keyword may be searched for the value.
 *
 * ⚠ BOUNDED ON PURPOSE, AND 2 IS THE WHOLE SAFETY MARGIN. Scanning forward is what lets an
 * alpha-prefixed id be found at all (see `extractAnnouncedIdentifiers`), but scanning far turns any
 * number downstream of the keyword into an "identifier" — `AUTH DECLINED STORE 1234` would start
 * disqualifying pairs on a store number. Two is enough for every observed real format
 * (`AUTH NO 123456` needs two; `REF: ABCD-1234` needs one) and stops there.
 */
const IDENTIFIER_VALUE_LOOKAHEAD_WORDS = 2;
/**
 * Separator punctuation between a keyword, its filler and its value. Carries no meaning.
 *
 * ⚠ THESE ARE DENY-BY-DEFAULT (anything not alphanumeric is a separator), NOT AN ALLOWLIST OF
 * PUNCTUATION — because an unlisted character MANUFACTURES A CONTRADICTION. A character the pattern
 * does not recognize stays glued to the value, so `REF/998877` extracted `"/998877"` while the posted
 * row's `REF 998877` extracted `"998877"`; the two "disagree", and `hasConflictingIdentifier`
 * disqualifies the pair OUTRIGHT, at merchant containment 1.0, before any other axis is consulted.
 *
 * **This was written as an allowlist twice, and reopened twice.** First hash, colon and whitespace
 * only — which switched the whole rule off for `REF-12345` and `TRACE NO. 5551212`. Then a widened
 * list adding dot, hyphen, star, slash, equals and parentheses — which still missed pipe, tilde,
 * backslash, plus and at-sign, and, one token to the right, missed dot and hyphen *after a filler
 * word* (`TRACE NO.5551212`), separators that same list already claimed. Enumerating cannot close a
 * class whose members are "whatever a bank happens to print", so the rule is inverted: a separator is
 * anything that is not part of a value, and values are alphanumeric.
 *
 * Rendering differences must read as AGREEMENT; only differing VALUES are evidence. Interior
 * punctuation is still KEPT, which is what keeps `ABCD-1234` distinguishable from `WXYZ-1234` —
 * splitting there would collapse both to `1234` and re-hide a real contradiction.
 *
 * (Measured on the replica 2026-09-08: the separators the previous allowlist missed appear after an
 * identifier keyword in 0 of 271,002 production transactions over 90 days, against 16,549 using the
 * handled ones, and a batch-shaped replay over 19,670 retracted rows moves 0 adoptions either way. So
 * this closes a latent hole, not a live one — which is the reason to close the CLASS rather than add
 * five more characters and wait for the sixth.)
 *
 * ⚠ Both patterns assume already-lowercased text (`extractAnnouncedIdentifiers` lowercases once, up
 * front) but are written to survive a caller that forgets.
 */
const LEADING_SEPARATORS = /^[^a-zA-Z0-9]+/;
/** Punctuation clinging to the ends of a candidate value. Interior punctuation is KEPT (`ABCD-1234`). */
const EDGE_PUNCTUATION = /^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g;
/**
 * Date shapes, stripped from a candidate value before it is asked whether it carries a digit.
 *
 * ⚠ A DATE IS NOT A PER-TRANSACTION IDENTIFIER, AND THE TWO PASSES HAD DRIFTED APART ON THAT. The
 * tokenizer removes dates precisely because "they vary per occurrence"; the identifier pass reads the
 * RAW description, so a dated ACH addenda satisfied "≥4 chars and contains a digit" and became an id:
 * `ACME PAYROLL REF*ASHT*FRESNO*12/2025*TAX REFUND` and its `01/2026` sibling read as CONTRADICTING
 * reference numbers and were disqualified outright, at any similarity. Real shape — it appears in the
 * committed bill-finder fixtures (`IRS TREAS 310 TAX REF REF*ASHT*FRESNO*…`).
 *
 * It fails in the safe direction (a missed adoption), which is why it is not higher severity, but a
 * varying field is the opposite of an identifier and must not be read as one.
 *
 * ⚠ The `MM/YYYY` arm is the one the real fixture needs — `12/2025` has a single slash, so a
 * `d/d/d`-only pattern (the tokenizer's own shape) does NOT match it and the value sails through.
 * Word-anchored so it cannot eat a slash out of the middle of a genuine id (`12/34567` is untouched).
 */
const DATE_SHAPES_IN_VALUE = /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b|\b\d{1,2}\/\d{2,4}\b|\b\d{4}-\d{2}-\d{2}\b/g;
/**
 * Keyword matchers, compiled ONCE at module load.
 *
 * ⚠ They used to be built per description — 15 `RegExp` constructions per call, 30 per candidate pair,
 * measured at 9.5 µs/pair which is ~10s over a 1M-pair production slice. That is the same cost this
 * path removed `getSimilarityScores` to avoid, so paying it here would have been self-defeating.
 * `String.matchAll` clones the regex internally, so sharing a `g`-flagged instance is safe.
 *
 * The trailing `(\d{1,2})?` is the ordinal described on `IDENTIFIER_KEYWORDS_BY_CLASS` — captured, not
 * discarded, because it names the field. It cannot swallow a longer keyword's stem (`\btrans(\d{1,2})?\b`
 * still fails on `transaction`, since a letter follows) and it cannot eat into a glued id
 * (`REF12345678` has no word boundary after either `ref` or `ref12`, exactly as before).
 */
const IDENTIFIER_KEYWORD_MATCHERS = Object.entries(IDENTIFIER_KEYWORDS_BY_CLASS).flatMap(([identifierClass, keywords]) => keywords.map((keyword) => ({ identifierClass, pattern: new RegExp(`\\b${keyword}(\\d{1,2})?\\b`, "g") })));
/**
 * The first identifier-shaped word within `IDENTIFIER_VALUE_LOOKAHEAD_WORDS` of a keyword, or null.
 *
 * ⚠ THIS SCANS FORWARD, AND THAT IS THE FIX FOR A SILENT HOLE. It was one regex that captured the
 * single alphanumeric run after the keyword and dropped the match if that run carried no digit — but
 * the global cursor had already moved past it, so no later run was ever examined. The consequence:
 * `REF: ABCD-1234` and `REF: ABCD-5678` both extracted NOTHING, reported no conflict, and paired at
 * STRONG identity 1.0 — two different transfers, adopted, with no amount corroboration either.
 *
 * Interior punctuation is preserved so `ABCD-1234` stays distinguishable from `WXYZ-1234`; splitting
 * on the hyphen would collapse both to `1234` and re-hide the contradiction.
 */
function findIdentifierValueAfterKeyword(textAfterKeyword) {
    const words = textAfterKeyword.replace(LEADING_SEPARATORS, "").split(/\s+/);
    let examined = 0;
    for (const word of words) {
        if (examined >= IDENTIFIER_VALUE_LOOKAHEAD_WORDS)
            return null;
        const trimmed = word.replace(EDGE_PUNCTUATION, "");
        if (trimmed.length === 0)
            continue; // pure punctuation — not a word, doesn't spend the budget
        if (IDENTIFIER_FILLER_WORDS.has(trimmed))
            continue; // `NO` / `NUMBER` — likewise
        examined += 1;
        const value = trimmed.replace(FILLER_GLUED_TO_VALUE, "");
        if (value.length < MIN_IDENTIFIER_VALUE_CHARS)
            continue;
        // The digit test runs on the value with DATE shapes removed: a value whose only digits are a date
        // is a varying field, not an identifier. See `DATE_SHAPES_IN_VALUE`.
        if (IDENTIFIER_VALUE_MUST_CONTAIN_DIGIT && !/\d/.test(value.replace(DATE_SHAPES_IN_VALUE, "")))
            continue;
        return value;
    }
    return null;
}
/**
 * Reads the per-transaction identifiers a description ANNOUNCES, as class → the set of values stated
 * for that class.
 *
 * A match is `<keyword>` as a whole word, then any run of separator punctuation, then an OPTIONAL
 * filler word (`NO` / `NUMBER` / …), then the value — the next run of alphanumerics. Multiple
 * statements of one class are all kept (a description can legitimately quote two), because a conflict
 * is decided between SETS: rows agree if they share any value for the class.
 *
 * ⚠ THE SEPARATOR AND FILLER ARE BOTH WIDER THAN THEY LOOK, AND BOTH HAD TO BE. The first version
 * accepted only `[#:\s]`, which meant `hasConflictingIdentifier` silently returned `false` — no
 * conflict, adopt — for every feed writing `AUTH NO 123456`, `REF NUMBER 998877`, `TRACE NO. 5551212`
 * or `REF-12345`. That is the rule this module calls the only thing that rejects the corpus's
 * `MUST NOT PAIR` case, switched off by a hyphen. Pinned by `deleteReplaceIdentity.test.ts`.
 */
const IDENTIFIER_CACHE_MAX_ENTRIES = 5000;
const identifierCache = new Map();
export function extractAnnouncedIdentifiers(description) {
    var _a;
    // Memoized for the same reason `merchantIdentityTokens` is: the recognizer is O(old × new), so each
    // description is re-scanned once per PAIR where once per DESCRIPTION would do. After the regex
    // hoisting this was the ONLY un-memoized piece left and therefore ~96% of the per-pair cost
    // (2.9 µs of 3.0 µs). Same bounded, clear-on-overflow cache — this runs in a long-lived process.
    // ⚠ The returned map is shared with every caller; treat it as READ-ONLY.
    const cached = identifierCache.get(description);
    if (cached)
        return cached;
    const found = new Map();
    const lowered = description.toLowerCase();
    for (const { identifierClass, pattern } of IDENTIFIER_KEYWORD_MATCHERS) {
        for (const match of lowered.matchAll(pattern)) {
            const value = findIdentifierValueAfterKeyword(lowered.slice(match.index + match[0].length));
            if (value === null)
                continue;
            // The ordinal (`Ref1` → `reference1`) names the FIELD, so it belongs in the key — see
            // `IDENTIFIER_KEYWORDS_BY_CLASS`. An unnumbered keyword keeps the bare class name it always had,
            // so nothing that already extracted changes key.
            const classKey = `${identifierClass}${(_a = match[1]) !== null && _a !== void 0 ? _a : ""}`;
            // A description can legitimately quote two ids of one class, and a Set keeps that idempotent
            // rather than something the keyword ordering has to be careful about.
            if (!found.has(classKey))
                found.set(classKey, new Set());
            found.get(classKey).add(value);
        }
    }
    if (identifierCache.size >= IDENTIFIER_CACHE_MAX_ENTRIES)
        identifierCache.clear();
    identifierCache.set(description, found);
    return found;
}
/**
 * Do these two descriptions state CONTRADICTORY values for the same identifier class?
 *
 * Only a class present on BOTH sides can contradict. One row quoting an id the other omits is the
 * ordinary pending→posted shape (the pending often carries no id at all), so absence is never
 * evidence against a pair — that asymmetry is the same reasoning containment rests on.
 *
 * ⚠ THE KNOWN COST, ACCEPTED DELIBERATELY: a bank that stamps its posted row with a NEW id for a
 * purchase it genuinely is replacing gets declined here, at any similarity. That is a missed
 * adoption — the direction the ADR chooses on purpose ("collapsing two genuine charges hides real
 * money; a redundant chore does not"), and the direction the aggregator link already covers for the
 * 79% of the harm population where Plaid states the supersession outright. Do not soften this rule
 * to recover those; it is the only thing that rejects the corpus's `MUST NOT PAIR` case.
 */
export function hasConflictingIdentifier(descriptionA, descriptionB) {
    const a = extractAnnouncedIdentifiers(descriptionA);
    const b = extractAnnouncedIdentifiers(descriptionB);
    for (const [identifierClass, valuesA] of a) {
        const valuesB = b.get(identifierClass);
        if (!valuesB)
            continue; // absent on one side is not a contradiction
        const shareAValue = [...valuesA].some((v) => valuesB.has(v));
        if (!shareAValue)
            return true;
    }
    return false;
}
/**
 * Rail / transaction-STATE words, dropped on top of the shared tokenizer's own noise classes.
 *
 * ⚠ SPECIFIC TO THIS AXIS, WHICH IS WHY IT IS NOT IN `merchantTokens.ts`. The bill/income detector
 * compares two rows of the SAME kind (a bill this month against the same bill last month), so a rail
 * verb there is at worst inert. We compare a PENDING row against the POSTED row that replaced it, and
 * these are exactly the words that differ *because* of that transition — the same purchase arrives as
 * `Pre auth  BETOS EAST LOC:` and comes back as `D/C SETTLEMENT  BETOS EAST LOC: SALT LAKE CITUT`.
 * Counting them as merchant identity charges a pair for the very state change it is being asked about.
 *
 * Every entry is a word that describes the transaction's handling, never a merchant's distinguishing
 * word. Keep it that way: a merchant genuinely called "Credit Union …" must not be hollowed out here.
 *
 * ⚠ `credit` AND `purchase` WERE IN THIS SET AND HAVE BEEN REMOVED — the rule above was written and
 * then immediately broken by the set below it. With `credit` stripped, `CREDIT UNION 1` reduced to
 * `[union]` and adopted onto `WESTERN UNION` at containment 1.0: a real credit union hollowed out to
 * a generic word, which is the exact failure the sentence above forbids. Both are ordinary merchant
 * words (`… CREDIT UNION`, `PURCHASE POWER`) as well as rail verbs, and the rail sense costs little
 * to keep — `Recurring Debit Purchase QUICKQUACK` still agrees with `QUICKQUACK` on the merchant
 * token. Measured on prod: 12.9% of retracted descriptions reduce to a single token, ~25% of those
 * only because this set stripped the rest. Before adding a word here, ask whether any merchant is
 * named with it.
 *
 * ⚠ **MEASURE A CHANGE TO THIS SET BATCH-SHAPED, over whole accounts at once — a single-retraction
 * replay reports the wrong answer.** Re-measured that way (replica 2026-09-08, 30 days, 19,670
 * retracted rows / 773 accounts): restoring BOTH words is a wash — 46 adoptions gained, 46 lost,
 * precision unmoved at 93.86% — and restoring only `purchase` is slightly worse (−8 net). So the
 * removal is kept on the evidence being neutral, NOT on it being a measured win; an earlier
 * single-retraction replay made it look like the best of three variants, which it is not. The same
 * harness shape distorted `MIN_AGREED_MERCHANT_TOKENS`'s weak-identity figures — see
 * ADR: delete-and-replace-recognition, axis 2, for why the shape changes the answer.
 */
const PENDING_POSTED_RAIL_STOPWORDS = new Set([
    "pending",
    "preauth",
    "pre",
    "auth",
    "settlement",
    "debit",
    "recurring",
    "pos",
    "pri",
    "loc",
    "dc",
]);
/**
 * Words that are real evidence of AGREEMENT but not of IDENTITY — the generic vocabulary a bank uses
 * when it has no merchant to name.
 *
 * ⚠ THIS SET IS NOT DROPPED, AND THE DIFFERENCE FROM A STOPWORD IS THE WHOLE POINT. `deposit` /
 * `withdrawal` / `transfer` are frequently ALL the identity a description carries — `TELLER DEPOSIT`
 * → `DEPOSIT`, `Deposit` → `Deposit - CLASSIC COLLISION`, `WITHDRAWAL` → `WITHDRAWAL 08/12`. Those are
 * genuine replacements, and they adopt today on exactly one agreed word plus an equal amount. Stripping
 * these words as noise was tried first and measured on a 30-day Plaid replay: it **lost 31 true
 * adoptions of that shape** while fixing the false ones. So they still count toward containment.
 *
 * What they must NOT do is make a pair STRONG, because strong identity waives the amount check
 * (`IdentityStrength`). That is what produced T-403's second false adoption: `Deposit from Vacation …`
 * and `Deposit from Emergency …` agreed on `deposit` + `from` — two tokens, therefore strong, therefore
 * the amounts were free to differ — and a +$60 transfer's decision was adopted onto a +$100 one. The
 * only words that could have distinguished them (the source-account names) were the two the rule
 * ignored. Measured on the same replay, treating generic-only agreement as WEAK costs **0** of those 31
 * adoptions, because every one of them has an exactly equal amount to corroborate it.
 *
 * Read it as: these words can carry a pair, but they can never carry it PAST the amount.
 *
 * ⚠ `from` sits here rather than in the shared tokenizer's envelope list beside its twin `to`, because
 * moving a word into that set silently re-tunes the bill finder (see merchantTokens.ts).
 *
 * ⚠ **THE BAR HERE IS NOT THE STOPWORD SET'S BAR, AND CLAIMING IT WAS WAS WRONG.** An earlier revision
 * of this comment said no merchant is named with these words. That is false — T-MOBILE, CASTLE BRANCH,
 * BRANCH MESSENGER, MOBILE MINI all exist. The stopword set below must meet that bar because it DELETES
 * a word; this set only declines to let one confer strength, so the question it has to answer is the
 * weaker one: *in a pending-vs-posted comparison, does this word tell us WHICH transaction?* Measured
 * over 130,133 real descriptions (30-day Plaid window), the answer is no — these are rail vocabulary
 * essentially everywhere they appear:
 *
 *   `online` 357× (`GAP ONLINE`) · `mobile` 349× (`payment thank you-mobile`, `mobile purchase MBTA`,
 *   `capital one mobile pymt`, `mobile deposit`) · `share` 288× (`… from Share Savings`) ·
 *   `banking` 245× (`Banking Office`) · `atm` 170× (`ATM Fee - Withdrawal`) · `other` 124×
 *   (`Transfer from Shares TFR FR OTHER`)
 *
 * ⚠ **`branch` WAS REMOVED on that evidence, and it is the shape to watch for.** It occurred ONCE in
 * 130,133 rows, as `CASTLE BRANCH …` — a merchant. A word that is rail vocabulary in theory and a
 * merchant name in practice belongs nowhere near this set.
 *
 * ⚠ **The accepted cost, stated rather than discovered later:** a merchant whose name reduces to one
 * distinguishing word plus a channel word — `Dunkin' Mobile` (145×), `GAP ONLINE` — is now WEAK, so an
 * amount-revised settlement of one (Dunkin' has in-app tipping) is declined rather than adopted. That is
 * a missed adoption, the direction the ADR chooses on purpose; a 30-day Plaid replay shows no measurable
 * loss, but the window may simply not contain one.
 */
const GENERIC_TRANSACTION_VOCABULARY = new Set([
    "deposit",
    "deposits",
    "withdrawal",
    "withdrawals",
    "withdraw",
    "transfer",
    "transfers",
    "tfr",
    "xfer",
    "from",
    "checking",
    "savings",
    "share",
    "shares",
    "other",
    "teller",
    "atm",
    "mobile",
    "online",
    "banking",
    "hold",
]);
/**
 * Shortest token we will accept as a TRUNCATION of a generic word.
 *
 * ⚠ WITHOUT THIS RULE THE WHOLE SET IS BYPASSABLE BY A TRUNCATING FEED, AND WITH THE OBVIOUS VERSION OF
 * IT THE CURE IS WORSE. `merchantTokensAgree` pairs a token with a longer one it prefixes
 * (`MIN_TRUNCATION_CHARS = 4`), so `WITHDRAWA FROM CHECKIN` agreed with `WITHDRAWAL FROM CHECKING` on
 * three tokens while only the two truncated ones were counted as DISTINGUISHING — reinstating exactly
 * the T-403 defect for any feed that clips descriptions.
 *
 * But "a token that prefixes a generic word is generic" cannot be the rule at 4 characters. Measured
 * over 130,133 real descriptions (30-day Plaid window): the 4-5 character prefixes are
 * **`check` (2,333)**, **`bank` (984)** — BANK OF AMERICA, US BANK — `tran`/`trans` (449) and `with`
 * (95), all of which are merchant or English words that must keep their identity. At 6+ characters the
 * population is only `saving` (25) and `deposi` (11), which are unambiguously clipped rail verbs.
 *
 * So 6 is where the two populations separate on this data, not a round number — and the failure
 * direction either way is a WEAK verdict (an adoption that needs an exact amount), never a wrong one.
 */
const MIN_GENERIC_TRUNCATION_CHARS = 6;
/** Bounded memo, same rationale as the token and identifier caches above. */
const GENERIC_TOKEN_CACHE_MAX_ENTRIES = 5000;
const genericTokenCache = new Map();
/**
 * Is this token the bank's generic vocabulary — spelled out, or clipped by a truncating feed?
 *
 * Only checks truncations in the one direction that can be one: the token being a PREFIX of a generic
 * word. The reverse (a generic word prefixing the token) would swallow real merchants that begin with
 * one — `TRANSFERWISE`, `DEPOSITPHOTOS` — which is the same hollowing-out this module already had to
 * undo for `credit` and `purchase`.
 */
function isGenericToken(token) {
    const cached = genericTokenCache.get(token);
    if (cached !== undefined)
        return cached;
    let generic = GENERIC_TRANSACTION_VOCABULARY.has(token);
    if (!generic && token.length >= MIN_GENERIC_TRUNCATION_CHARS) {
        for (const word of GENERIC_TRANSACTION_VOCABULARY) {
            if (word.length > token.length && word.startsWith(token)) {
                generic = true;
                break;
            }
        }
    }
    if (genericTokenCache.size >= GENERIC_TOKEN_CACHE_MAX_ENTRIES)
        genericTokenCache.clear();
    genericTokenCache.set(token, generic);
    return generic;
}
/**
 * Debris from a PENDING-only description template, dropped for the same reason as the rail verbs.
 *
 * ⚠ A REGEX RATHER THAN A SET BECAUSE THE TOKENS ARE NUMBERED. Several credit unions stamp every
 * pending with `<merchant> / Ref1: … Ref2: … Ref3: … Expires: …`. The digits are stripped by the shared
 * tokenizer as noise, but the `ref1` / `ref2` / `ref3` stems survive it (the drop-alphanumeric-hash rule
 * needs 5 characters and these are 4), so FOUR boilerplate tokens counted as merchant identity — enough
 * on their own to clear both floors. Measured in prod: PARAMOUNT+ scored containment 0.80 and `strong`
 * against three unrelated merchants (T-403), because the only thing they shared was the template.
 *
 * This is the second, independent guard on that shape — `IDENTIFIER_KEYWORDS_BY_CLASS` now reads the
 * `RefN` values and disqualifies the pair outright. Both are worth having: the identifier rule needs
 * the values to be PRESENT and DIFFERENT, and this one holds when a feed omits them or repeats one.
 *
 * ⚠ The ordinal is REQUIRED (`\d{1,2}`, not `\d{0,2}`), which keeps this scoped to the template it was
 * measured on. Permitting zero digits also stripped the bare token `ref` — `ACH REF 998877 NETFLIX` lost
 * a word it shares with its own settlement, demoting a same-reference amount-revised pair from strong to
 * weak. Fails safe, but it is a rule firing outside the population that justifies it.
 * ⚠ It cannot hollow out a real name: `refund` does not match (the stem must be followed by digits),
 * and `expires` is a field label no merchant is called.
 */
const PENDING_TEMPLATE_DEBRIS = /^(?:ref\d{1,2}|expires)$/;
/**
 * Bounded memo for `merchantIdentityTokens`.
 *
 * ⚠ The recognizer is O(old × new): every retracted entry is compared against every candidate, so each
 * description is re-tokenized once per PAIR when once per DESCRIPTION would do (~1M pairs in a 90-day
 * production slice, at ~3 µs each). Tokenization is pure, so caching it is safe.
 *
 * Cleared wholesale on overflow rather than evicting per entry: this runs inside a long-lived server
 * process, so an unbounded map would be a slow leak, and the access pattern it exists for — one batch
 * of descriptions compared against each other — is served fine by a generation that resets.
 */
const MERCHANT_TOKEN_CACHE_MAX_ENTRIES = 5000;
const merchantTokenCache = new Map();
/** The shared merchant tokens, minus the pending-vs-posted rail verbs and pending-template debris. */
function merchantIdentityTokens(description) {
    const cached = merchantTokenCache.get(description);
    if (cached)
        return cached;
    const tokens = normalizeDescriptionTokens(description).filter((tok) => !PENDING_POSTED_RAIL_STOPWORDS.has(tok) && !PENDING_TEMPLATE_DEBRIS.test(tok));
    if (merchantTokenCache.size >= MERCHANT_TOKEN_CACHE_MAX_ENTRIES)
        merchantTokenCache.clear();
    merchantTokenCache.set(description, tokens);
    return tokens;
}
/**
 * How many merchant words two descriptions actually share, and out of how many.
 *
 * `containment` is the overlap-coefficient form — agreed tokens over the SHORTER side — rather than
 * Jaccard or Dice, so tokens present on only one side cost the pair nothing (see the module doc).
 * Matching is greedy and ONE-TO-ONE: a token already claimed cannot be claimed again, or a repeated
 * word would report agreement the pair does not have.
 *
 * The raw `agreed` count is returned alongside because the ratio alone is not safe at small token
 * counts — see `MIN_AGREED_MERCHANT_TOKENS`.
 */
export function merchantAgreement(descriptionA, descriptionB) {
    const tokensA = merchantIdentityTokens(descriptionA);
    const tokensB = merchantIdentityTokens(descriptionB);
    if (tokensA.length === 0 || tokensB.length === 0) {
        /*
        ⚠ BOTH SIDES EMPTY AND THE DESCRIPTIONS EQUIVALENT IS THE STRONGEST SIGNAL THERE IS, NOT THE
        WEAKEST — and returning 0 here was a REGRESSION on the population that already worked. Plenty of
        real merchant names survive tokenization as nothing: `H-E-B` loses every token to the
        drop-single-characters rule, and `MURPHY7596ATWALMART` / `CUBE4ME` / `GRAETERS43` to the
        drop-long-alphanumeric-hash rule. When the retracted and posted descriptions are the SAME string,
        "no merchant words survived" says something about the tokenizer, not about the pair. Measured on
        prod: 0.31% of retracted descriptions normalize to nothing, and 19 adoptions in 90 days were lost
        this way with the two descriptions byte-identical AND the amounts equal.
    
        `shorterLength: 0` marks this as identity resting on NO surviving token, which
        `scoreDeleteReplaceIdentity` treats as WEAK — so it still has to be corroborated by an exact
        amount. Every other empty case (one side empty, or two different unparseable descriptions) keeps
        failing closed at containment 0.
        */
        const bothEmptyAndEquivalent = tokensA.length === 0 && tokensB.length === 0 && descriptionsAreEquivalent(descriptionA, descriptionB);
        return { agreed: 0, agreedDistinguishing: 0, shorterLength: 0, containment: bothEmptyAndEquivalent ? 1 : 0 };
    }
    const [short, long] = tokensA.length <= tokensB.length ? [tokensA, tokensB] : [tokensB, tokensA];
    const claimed = new Set();
    let agreed = 0;
    // TWO PASSES — exact matches are claimed BEFORE truncation matches, and the order is load-bearing.
    // A single greedy pass let an early token consume a partner that a later token needed exactly:
    // `TRANS TRANSFER` vs `TRANSFER TRANSMIT` scored 1 agreed while `TRANSFER TRANS` vs the same scored
    // 2, so the answer depended on the order the bank happened to write the words in.
    //
    // ⚠ SPLITTING THE PASSES WAS NOT ENOUGH ON ITS OWN — the truncation pass is still greedy, so two
    // MUTUALLY-PREFIXING tokens consumed each other's partner and the same order-dependence survived one
    // level down: `TRANS TRANSF` vs `TRANSFER TRANSMIT` declined while `TRANSF TRANS` vs the same scored
    // a perfect strong match. Reordering two words in the retracted description flipped decline into
    // adopt, over exactly the truncation debris this rule exists for. The pass therefore claims the
    // LONGEST unmatched token first (ties broken alphabetically): a longer prefix is more specific
    // evidence, and sorting makes the result independent of the order the bank wrote the words in.
    const unmatched = [];
    // Counted alongside `agreed`: how many agreements rest on a word that actually NAMES something,
    // rather than on the bank's generic vocabulary. Only this count may confer STRONG identity — see
    // `GENERIC_TRANSACTION_VOCABULARY`.
    let agreedDistinguishing = 0;
    // ⚠ BOTH SIDES ARE TESTED, NOT JUST THE SHORT ONE. The two tokens of an agreement can be spelled
    // differently — the truncation pass pairs `checkin` with `checking` — so testing only the side this
    // loop happens to iterate let a clipped generic word count as identity (T-403 review, finding 1).
    const credit = (shortToken, longToken) => {
        agreed += 1;
        if (!isGenericToken(shortToken) && !isGenericToken(longToken))
            agreedDistinguishing += 1;
    };
    for (const token of short) {
        const exactIdx = long.findIndex((candidate, idx) => !claimed.has(idx) && candidate === token);
        if (exactIdx === -1) {
            unmatched.push(token);
            continue;
        }
        claimed.add(exactIdx);
        credit(token, token); // an exact match: the two spellings are the same by definition
    }
    for (const token of [...unmatched].sort((a, b) => b.length - a.length || a.localeCompare(b))) {
        const partnerIdx = long.findIndex((candidate, idx) => !claimed.has(idx) && merchantTokensAgree(token, candidate));
        if (partnerIdx === -1)
            continue;
        claimed.add(partnerIdx);
        credit(token, long[partnerIdx]);
    }
    return { agreed, agreedDistinguishing, shorterLength: short.length, containment: agreed / short.length };
}
/**
 * Are two descriptions the same string once the noise this module already treats as noise is folded
 * away — case and whitespace runs?
 *
 * Used only for the both-sides-empty case above, where there are no tokens left to compare.
 */
function descriptionsAreEquivalent(a, b) {
    const fold = (s) => s.toLowerCase().replace(/\s+/g, " ").trim();
    const folded = fold(a);
    // A pair of EMPTY descriptions is not evidence of anything — two rows carrying no description at
    // all are "equal" in the trivial sense and share no information. The case this branch exists for is
    // a description with real content that the tokenizer erased (`H-E-B`, `MURPHY7596ATWALMART`).
    if (folded.length === 0)
        return false;
    return folded === fold(b);
}
/**
 * Containment over merchant words, in [0, 1]: what fraction of the SHORTER token list finds a partner
 * in the longer one. See `merchantAgreement`, of which this is the ratio half.
 */
export function merchantContainment(descriptionA, descriptionB) {
    return merchantAgreement(descriptionA, descriptionB).containment;
}
/**
 * The minimum containment at which we call two descriptions the same merchant.
 *
 * ⚠ IT IS NOT THE OLD `descriptionSimilarity` BAR UNDER A NEW NAME, and the difference is visible in
 * the measurement rather than the value. Across all **19** real pairs committed in this repo — the 5
 * `expect: "replace"` corpus cases plus the 14 production replacements pinned in
 * `suggestionTools.test.ts` (§ "Real-life examples of description changes" and § casing) — this axis
 * reads as very nearly **bimodal**:
 *
 *  - **Genuine replacements: 0.6 → 1.0**, eighteen of the nineteen at exactly 1.0. Descriptions that
 *    gain a store number, a city, a state, a ZIP or a `Card# **0000` tail cost the pair nothing, which
 *    is the whole reason containment replaced a symmetric coefficient. The binding case is
 *    `AMAZON MKTPLACE PMTS Amzn.com/billUS` → `AMAZON MKTPL*XXXXXXXXX Amzn.com/billWA` at **0.600**,
 *    where a truncation and a state-suffix swap eat two of five tokens.
 *  - **Genuinely different merchants: 0.0–0.25.** Unrelated descriptions share few or no merchant
 *    words. That separation is what bigram overlap could not produce at any threshold (its ratio was
 *    flat at 1.2–1.7 from 0.2 to 0.9, and it scored two unrelated transfers 0.833).
 *
 * `0.5` sits in the gap between those two populations rather than being tuned to either edge, and the
 * floor is not doing fine-grained discrimination — it should not be asked to.
 *
 * ⚠ THE FLOOR ALONE IS NOT SAFE AT SMALL TOKEN COUNTS — `MIN_AGREED_MERCHANT_TOKENS` is the other
 * half of the rule, and the two guard opposite ends. Read that constant before changing this one.
 *
 * ⚠ THIS IS THE AXIS THAT CANNOT ANSWER T-79. A month-old delete and a fresh recurring charge at the
 * same merchant score 1.0 here and always will — the merchant words are the same because the
 * merchant IS the same. RECENCY (`deleteReplaceRecency.ts`) is what rejects that pair — this axis is
 * deliberately more permissive than what it replaced, and relies on that one to be safe.
 */
export const MIN_MERCHANT_CONTAINMENT = 0.5;
/**
 * How many merchant words must actually AGREE, independent of the ratio.
 *
 * ⚠ THIS EXISTS BECAUSE A RATIO IS MEANINGLESS AT N=2. A two-token description sharing ONE word
 * scores 0.5 and clears `MIN_MERCHANT_CONTAINMENT` — so `BLUE BOTTLE` and `BLUE APRON` read as the
 * same merchant, as do `MAR STORE` and `MARATHON STORE`. On the same account, on the same day, two
 * different merchants whose names open with the same word is not a contrived shape, and adopting
 * across it is the failure direction the ADR cares most about ("collapsing two genuine charges hides
 * real money; a redundant chore does not").
 *
 * The carve-out: a shorter side of exactly ONE token may agree on that one token alone. A description
 * that normalizes to a single word has usually had everything but the merchant name stripped, so that
 * word is often the identity — `WALMART`, `QUICKQUACK`, `NORTON`. Requiring two there would decline
 * those **three** real production replacements — ⚠ and note they are admitted *at an exact amount
 * only*, because the carve-out yields WEAK identity (below), so it is no longer the whole story for
 * them. (An earlier version of this comment said FOUR and named the truncated ALPINE pair as the
 * fourth; ALPINE is `{agreed: 4, shorterLength: 4}` — STRONG, and never reached this carve-out.)
 *
 * ⚠ BUT A LONE TOKEN IS *WEAK* EVIDENCE, AND THE CARVE-OUT ALONE WAS A HOLE. `AMAZON.COM` reduces to
 * `[amazon]` and agreed perfectly with `AMAZON PRIME VIDEO 8663924`; since phase 2 removed the amount
 * gate, nothing else held the pair together, so a sorted $9 entry could adopt onto a $250 charge —
 * the money-hiding direction the ADR ranks worst. 12.9% of retracted descriptions on prod reduce to
 * one token, so this is a wide path, not a corner. It is closed by `identityStrength` below: weak
 * identity must be corroborated by an EXACT amount.
 *
 * ⚠ **THIS CONSTANT NOW SETS TWO FLOORS, DELIBERATELY THE SAME NUMBER.** It gates `agreed` (may the pair
 * be considered at all) and `agreedDistinguishing` (may it be STRONG). They are the same number because
 * it is the same argument in both places — a ratio is meaningless at N=1, and one word is one word
 * whether or not the other words were generic. Splitting them into two constants was considered and
 * rejected: a second name with the same value invites the two to drift apart with no reasoning recorded
 * for why they differ. If you move this, you are moving BOTH floors — decide that on purpose.
 */
export const MIN_AGREED_MERCHANT_TOKENS = 2;
/** Containment at which we treat the two descriptions as naming the same merchant with no reservation. */
const PERFECT_CONTAINMENT = 1;
/**
 * The identity axis's verdict on one candidate pair.
 *
 * `null` means DISQUALIFIED — a contradicted identifier, or merchant words too far apart to be the
 * same merchant. Otherwise a containment score in [`MIN_MERCHANT_CONTAINMENT`, 1], where 1 means the
 * merchant words agree completely, plus the `strength` the caller must act on.
 */
export function scoreDeleteReplaceIdentity(p) {
    // FIRST, and unconditionally: a contradicted identifier outranks any amount of agreement
    // elsewhere. Two rows can read identically apart from the bank's own id and still be two separate
    // transactions — which is the corpus's `MUST NOT PAIR` case, at containment 1.0.
    if (hasConflictingIdentifier(p.retractedDescription, p.postedDescription))
        return null;
    const { agreed, agreedDistinguishing, shorterLength, containment } = merchantAgreement(p.retractedDescription, p.postedDescription);
    // NO surviving tokens on either side. `merchantAgreement` reports containment 1 only when the two
    // descriptions are equivalent strings, and 0 otherwise — so this admits the byte-identical pair the
    // tokenizer happened to erase, and nothing else. Weak, because there is no merchant word behind it.
    if (shorterLength === 0) {
        return containment >= PERFECT_CONTAINMENT ? { containment: 1, isPerfect: true, strength: "weak" } : null;
    }
    // Both halves of the rule, guarding opposite ends: the COUNT keeps a single shared word from
    // reading as agreement at N=2, the RATIO keeps a couple of incidental hits from carrying a long
    // description. Read both constants' docs before touching either.
    const enoughAgreed = agreed >= MIN_AGREED_MERCHANT_TOKENS || (shorterLength === 1 && agreed === 1);
    if (!enoughAgreed)
        return null;
    if (containment < MIN_MERCHANT_CONTAINMENT)
        return null;
    return {
        containment,
        isPerfect: containment >= PERFECT_CONTAINMENT,
        // ⚠ STRENGTH COUNTS ONLY THE DISTINGUISHING AGREEMENTS, not every agreement. Two rows agreeing on
        // `deposit` + `from` and nothing else have told us only that both are deposits — real evidence,
        // but not evidence of WHICH deposit, and strong identity is what waives the amount check. See
        // `GENERIC_TRANSACTION_VOCABULARY` for the production case and the replay that sized both sides.
        strength: agreedDistinguishing >= MIN_AGREED_MERCHANT_TOKENS ? "strong" : "weak",
    };
}
//# sourceMappingURL=deleteReplaceIdentity.js.map